import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import AnalyticsDashboard from "./AnalyticsDashboard";
import { toast } from "sonner";
import { Plus, BarChart3, Edit2, CheckCircle, Trash2, Lock, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Configuração central dos 6 grupos de parâmetros =====
// type: 'time' (HH:MM), 'number' (float/int), 'percent'
const PARAM_GROUPS = [
  {
    id: 'admin',
    title: '📋 Administrativo',
    description: 'Horários padrão de início e fim do expediente',
    fields: [
      { key: 'standard_pt_room_time', label: 'Horário padrão na sala de PT', type: 'time', placeholder: '06:00' },
      { key: 'standard_pt_issuance_duration', label: 'Tempo médio emissão PT', unit: 'min', type: 'number', placeholder: '30' },
      { key: 'standard_pt_approval_duration', label: 'Tempo médio aprovação PT', unit: 'min', type: 'number', placeholder: '45' },
      { key: 'standard_morning_start_time', label: 'Horário padrão início manhã', type: 'time', placeholder: '07:00' },
      { key: 'standard_lunch_duration_minutes', label: 'Duração padrão do almoço', unit: 'min', type: 'number', placeholder: '60' },
      { key: 'standard_daily_end_time', label: 'Horário padrão de encerramento', type: 'time', placeholder: '17:00' },
    ],
  },
  {
    id: 'sandblasting',
    title: '⚙️ Produtividade: Jateamento',
    description: 'Metas diárias e parâmetros operacionais de jateamento',
    fields: [
      { key: 'standard_sandblasting_hours_day', label: 'Horas de jateamento', unit: 'h/dia', type: 'number', step: 0.5, placeholder: '8.0' },
      { key: 'standard_columns_sandblasted_day', label: 'Colunas jateadas inteiras', unit: 'un/dia', type: 'number', placeholder: '5' },
      { key: 'standard_sandblasted_columns_width', label: 'Largura padrão das colunas', unit: 'm', type: 'number', step: 0.01, placeholder: '1.20' },
      { key: 'standard_sandblasted_columns_height', label: 'Altura padrão das colunas', unit: 'm', type: 'number', step: 0.01, placeholder: '3.50' },
      { key: 'standard_robot_advance_speed', label: 'Velocidade do robô', unit: '', type: 'number', step: 0.01, placeholder: '1.5' },
      { key: 'standard_turbine_speed', label: 'Velocidade das turbinas', unit: 'RPM', type: 'number', placeholder: '2800' },
      { key: 'standard_sandblasting_current', label: 'Corrente de jateamento', unit: 'A', type: 'number', step: 0.1, placeholder: '30.0' },
      { key: 'max_sandblasting_rework_percentage', label: 'Limite máx. de repassagem', unit: '%', type: 'number', step: 0.1, placeholder: '5.0' },
    ],
  },
  {
    id: 'painting',
    title: '🎨 Produtividade: Pintura',
    description: 'Metas diárias e dimensões padrão para pintura',
    fields: [
      { key: 'standard_painting_hours_day', label: 'Horas de pintura', unit: 'h/dia', type: 'number', step: 0.5, placeholder: '6.0' },
      { key: 'standard_columns_painted_day', label: 'Colunas pintadas inteiras', unit: 'un/dia', type: 'number', placeholder: '3' },
      { key: 'standard_painted_columns_width', label: 'Largura padrão das colunas', unit: 'm', type: 'number', step: 0.01, placeholder: '1.20' },
      { key: 'standard_painted_columns_height', label: 'Altura padrão das colunas', unit: 'm', type: 'number', step: 0.01, placeholder: '3.50' },
    ],
  },
  {
    id: 'operations',
    title: '🔧 Operações Técnicas',
    description: 'Tempos aceitáveis e vida útil de componentes',
    fields: [
      { key: 'standard_tool_change_time_minutes', label: 'Tempo padrão de troca de implemento', unit: 'min', type: 'number', placeholder: '20' },
      { key: 'standard_turbine_kit_lifespan_days', label: 'Vida útil padrão do Kit Turbina', unit: 'dias', type: 'number', placeholder: '30' },
      { key: 'standard_sealing_kits_min_stock', label: 'Estoque mínimo de kits de vedação', unit: 'un', type: 'number', placeholder: '4' },
    ],
  },
  {
    id: 'materials',
    title: '📦 Consumo de Materiais',
    description: 'Consumo diário esperado e limites de perda',
    fields: [
      { key: 'standard_grit_consumption_day', label: 'Granalha (abrasivo)', unit: 'kg/dia', type: 'number', step: 0.1, placeholder: '150' },
      { key: 'max_grit_loss_day', label: 'Perda máxima de granalha', unit: 'kg/dia', type: 'number', step: 0.1, placeholder: '5.0' },
      { key: 'standard_paint_consumption_day', label: 'Tinta', unit: 'L/dia', type: 'number', step: 0.1, placeholder: '50' },
      { key: 'standard_component_ab_consumption_day', label: 'Componentes A e B', unit: 'L/dia', type: 'number', step: 0.1, placeholder: '10' },
      { key: 'standard_inhibitor_consumption_day', label: 'Inibidor de corrosão', unit: 'L/dia', type: 'number', step: 0.1, placeholder: '5' },
      { key: 'standard_solvent_consumption_day', label: 'Solvente', unit: 'L/dia', type: 'number', step: 0.1, placeholder: '20' },
    ],
  },
  {
    id: 'quality',
    title: '⚠️ Qualidade & Impedimentos',
    description: 'Limites máximos e faixas aceitáveis de medição',
    fields: [
      { key: 'max_acceptable_impediments_day', label: 'Máx. impedimentos por dia', unit: 'qtd', type: 'number', placeholder: '2' },
      { key: 'max_impediment_minutes_day', label: 'Máx. tempo total em impedimentos', unit: 'min/dia', type: 'number', placeholder: '90' },
      { key: 'max_acceptable_rework_percentage', label: 'Máx. retrabalho', unit: '%', type: 'number', step: 0.1, placeholder: '5.0' },
      { key: 'roughness_target_min', label: 'Rugosidade — mínimo aceitável', unit: 'µm', type: 'number', step: 0.1, placeholder: '40' },
      { key: 'roughness_target_max', label: 'Rugosidade — máximo aceitável', unit: 'µm', type: 'number', step: 0.1, placeholder: '70' },
      { key: 'thickness_target_min', label: 'Espessura — mínimo aceitável', unit: 'µm', type: 'number', step: 0.1, placeholder: '200' },
      { key: 'thickness_target_max', label: 'Espessura — máximo aceitável', unit: 'µm', type: 'number', step: 0.1, placeholder: '400' },
    ],
  },
];

// Flat list dos campos numéricos (para conversão antes de enviar ao backend)
const ALL_NUMBER_FIELDS = PARAM_GROUPS.flatMap(g => g.fields.filter(f => f.type === 'number').map(f => f.key));
const ALL_TIME_FIELDS = PARAM_GROUPS.flatMap(g => g.fields.filter(f => f.type === 'time').map(f => f.key));

// Opções do 7º grupo — Itens de Almoxarifado
const WAREHOUSE_UNIT_OPTIONS = ['un', 'kg', 'g', 'L', 'mL', 'm²', 'm³', 'm'];
const WAREHOUSE_LIFESPAN_UNIT_OPTIONS = ['Dias', 'Horas', 'M²', 'Ciclos', 'Peças'];
const WAREHOUSE_DESTINATION_OPTIONS = [
  { value: 'sucatear', label: 'Sucatear' },
  { value: 'remanufaturar', label: 'Remanufaturar' },
  { value: 'repor', label: 'Repor' },
  { value: 'repor_urgencia', label: 'Repor com Urgência' },
];

const buildEmptyWarehouseItem = () => ({
  id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  name: '',
  unit: '',
  initial_stock: '',
  min_stock: '',
  lifespan_value: '',
  lifespan_unit: '',
  standard_destination: '',
  observations: '',
  procurement_sku: '',
});

// Estado inicial gerado a partir dos grupos
const buildEmptyFormData = () => {
  const base = {
    client_name: '',
    contract_number: '',
    service_location: '',
    deviation_tolerance_percentage: '',
  };
  PARAM_GROUPS.forEach(g => g.fields.forEach(f => { base[f.key] = ''; }));
  return base;
};

const ParametersPanel = () => {
  const [clients, setClients] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const [formData, setFormData] = useState(buildEmptyFormData());
  const [warehouseItems, setWarehouseItems] = useState([]);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/parameters`);
      // Garantir que response.data é um array
      if (Array.isArray(response.data)) {
        setClients(response.data);
      } else {
        console.error("Response is not an array:", response.data);
        setClients([]);
        toast.error("Formato de resposta inválido do servidor");
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      if (error.response?.status === 404) {
        // Nenhum cliente encontrado ainda
        setClients([]);
      } else {
        setClients([]);
        toast.error("Erro ao carregar clientes");
      }
    }
  };

  const openNewClientModal = () => {
    setEditingClient(null);
    setFormData(buildEmptyFormData());
    setWarehouseItems([]);
    setShowClientModal(true);
  };

  const openEditClientModal = (client) => {
    setEditingClient(client);
    // Converter nulls/undefined em '' para os inputs controlados
    const cleaned = { ...buildEmptyFormData(), ...client };
    Object.keys(cleaned).forEach(k => {
      if (cleaned[k] === null || cleaned[k] === undefined) cleaned[k] = '';
    });
    setFormData(cleaned);
    // Carregar warehouse_items do cliente (normalizar nulls em strings)
    const items = (client.warehouse_items || []).map(it => ({
      id: it.id || `tmp_${Math.random().toString(36).slice(2, 10)}`,
      name: it.name || '',
      unit: it.unit || '',
      initial_stock: it.initial_stock ?? '',
      min_stock: it.min_stock ?? '',
      lifespan_value: it.lifespan_value ?? '',
      lifespan_unit: it.lifespan_unit || '',
      standard_destination: it.standard_destination || '',
      observations: it.observations || '',
      procurement_sku: it.procurement_sku || '',
    }));
    setWarehouseItems(items);
    setShowClientModal(true);
  };

  const saveClient = async () => {
    if (!formData.client_name || !formData.contract_number || !formData.service_location) {
      toast.error("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    try {
      // Preparar dados - converter strings vazias em null para campos numéricos
      const dataToSend = { ...formData };

      // Incluir deviation_tolerance_percentage no conjunto de numéricos
      const numericFields = [...ALL_NUMBER_FIELDS, 'deviation_tolerance_percentage'];

      numericFields.forEach(field => {
        if (dataToSend[field] === '' || dataToSend[field] === undefined) {
          dataToSend[field] = null;
        } else if (dataToSend[field] !== null) {
          const n = parseFloat(dataToSend[field]);
          dataToSend[field] = Number.isNaN(n) ? null : n;
        }
      });

      ALL_TIME_FIELDS.forEach(field => {
        if (dataToSend[field] === '') {
          dataToSend[field] = null;
        }
      });

      // ===== Itens de Almoxarifado =====
      // Validar: nome e unidade são obrigatórios por item. Ignorar linhas 100% vazias.
      const cleanedItems = [];
      for (let i = 0; i < warehouseItems.length; i++) {
        const it = warehouseItems[i];
        const isFullyEmpty = !it.name && !it.unit && !it.initial_stock && !it.min_stock
          && !it.lifespan_value && !it.lifespan_unit && !it.standard_destination
          && !it.observations && !it.procurement_sku;
        if (isFullyEmpty) continue;
        if (!it.name || !it.unit) {
          toast.error(`Item ${i + 1} de Almoxarifado: Nome e Unidade são obrigatórios.`);
          return;
        }
        const toNum = (v) => (v === '' || v === null || v === undefined ? null : (Number.isNaN(parseFloat(v)) ? null : parseFloat(v)));
        cleanedItems.push({
          id: it.id && !it.id.startsWith('tmp_') ? it.id : undefined,
          name: it.name.trim(),
          unit: it.unit,
          initial_stock: toNum(it.initial_stock),
          min_stock: toNum(it.min_stock),
          lifespan_value: toNum(it.lifespan_value),
          lifespan_unit: it.lifespan_unit || null,
          standard_destination: it.standard_destination || null,
          observations: it.observations?.trim() || null,
          procurement_sku: it.procurement_sku?.trim() || null,
        });
      }
      dataToSend.warehouse_items = cleanedItems;

      if (editingClient) {
        await axios.put(`${API}/parameters/${editingClient.id}`, dataToSend);
        toast.success("Cliente atualizado com sucesso!");
      } else {
        await axios.post(`${API}/parameters`, dataToSend);
        toast.success("Cliente criado com sucesso!");
      }

      setShowClientModal(false);
      loadClients();
    } catch (error) {
      console.error("Error saving client:", error);
      let errorMessage = "Erro ao salvar cliente";

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const errors = error.response.data.detail.map(err => err.msg || err.message).join(", ");
          errorMessage = `Erro de validação: ${errors}`;
        }
      }

      toast.error(errorMessage);
    }
  };

  const finishClient = async (clientId) => {
    if (!window.confirm("Tem certeza que deseja marcar este cliente como Finalizado? O cliente não poderá mais ser editado.")) {
      return;
    }

    try {
      await axios.patch(`${API}/parameters/${clientId}/status?status=FINALIZADO`);
      toast.success("Cliente marcado como Finalizado!");
      loadClients();
    } catch (error) {
      toast.error("Erro ao finalizar cliente");
    }
  };

  const reopenClient = async (clientId) => {
    if (!window.confirm("Reabrir este cliente? Ele voltará ao status 'Em Andamento' e poderá ser editado novamente.")) {
      return;
    }

    try {
      await axios.patch(`${API}/parameters/${clientId}/status?status=EM_ANDAMENTO`);
      toast.success("Cliente reaberto com sucesso!");
      loadClients();
    } catch (error) {
      toast.error("Erro ao reabrir cliente");
    }
  };

  const deleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o cliente "${clientName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/parameters/${clientId}`);
      toast.success("Cliente excluído com sucesso!");
      loadClients();
    } catch (error) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const openAnalytics = async (client) => {
    setSelectedClient(client);
    setShowAnalyticsModal(true);
    loadAnalytics(client.id);
  };

  const loadAnalytics = async (clientId) => {
    setAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/performance`, {
        params: {
          client_id: clientId,
          ...dateRange
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Erro ao carregar análises");
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const formatVariance = (variance) => {
    if (variance === null || variance === undefined) return 'N/A';
    const sign = variance >= 0 ? '+' : '';
    const color = variance >= 0 ? 'text-green-600' : 'text-red-600';
    return <span className={color}>{sign}{variance}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-800">📊 Gerenciamento de Clientes e Parâmetros</h2>
        <Button
          onClick={openNewClientModal}
          data-testid="new-client-btn"
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Lista de Clientes */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes</CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="text-center py-12 text-slate-600">
              <p className="text-lg">Nenhum cliente cadastrado</p>
              <p className="text-sm mt-2">Clique em "Novo Cliente" para adicionar o primeiro cliente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left p-3 font-semibold">Nome do Cliente</th>
                    <th className="text-left p-3 font-semibold">Nº Contrato</th>
                    <th className="text-left p-3 font-semibold">Local</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Criado em</th>
                    <th className="text-center p-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr 
                      key={client.id} 
                      className={`border-b hover:bg-slate-50 ${client.status === 'FINALIZADO' ? 'opacity-60' : ''}`}
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="p-3">
                        <div className="font-medium">{client.client_name}</div>
                      </td>
                      <td className="p-3">{client.contract_number}</td>
                      <td className="p-3">{client.service_location}</td>
                      <td className="p-3">
                        <Badge 
                          variant={client.status === 'EM_ANDAMENTO' ? 'default' : 'secondary'}
                          className={client.status === 'EM_ANDAMENTO' ? 'bg-green-600' : 'bg-gray-500 gap-1'}
                        >
                          {client.status === 'EM_ANDAMENTO' ? (
                            'Em Andamento'
                          ) : (
                            <><Lock className="w-3 h-3" /> Finalizado</>
                          )}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {new Date(client.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAnalytics(client)}
                            data-testid={`analyze-btn-${client.id}`}
                            title="Analisar"
                          >
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                          </Button>
                          
                          {client.status === 'EM_ANDAMENTO' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditClientModal(client)}
                              data-testid={`edit-btn-${client.id}`}
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4 text-slate-600" />
                            </Button>
                          )}
                          
                          {client.status === 'EM_ANDAMENTO' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => finishClient(client.id)}
                              data-testid={`finish-btn-${client.id}`}
                              title="Concluir"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </Button>
                          )}

                          {client.status === 'FINALIZADO' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reopenClient(client.id)}
                              data-testid={`reopen-btn-${client.id}`}
                              title="Reabrir"
                            >
                              <RotateCcw className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteClient(client.id, client.client_name)}
                            data-testid={`delete-btn-${client.id}`}
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar Cliente */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? `Editar Cliente: ${editingClient.client_name}` : 'Novo Cliente'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente e os parâmetros operacionais padrão
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Dados do Cliente */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">🏢 Dados do Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="client_name">Nome do Cliente *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Ex: Petrobras S.A."
                  />
                </div>
                
                <div>
                  <Label htmlFor="contract_number">Número do Contrato *</Label>
                  <Input
                    id="contract_number"
                    value={formData.contract_number}
                    onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                    placeholder="Ex: CT-2025-001"
                  />
                </div>
                
                <div>
                  <Label htmlFor="service_location">Local da Prestação do Serviço *</Label>
                  <Input
                    id="service_location"
                    value={formData.service_location}
                    onChange={(e) => setFormData({...formData, service_location: e.target.value})}
                    placeholder="Ex: Plataforma P-77"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="deviation_tolerance_percentage">
                    🎚️ Tolerância de desvio <span className="text-xs text-slate-500">(padrão: 10%)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="deviation_tolerance_percentage"
                      data-testid="deviation-tolerance-input"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.deviation_tolerance_percentage}
                      onChange={(e) => setFormData({...formData, deviation_tolerance_percentage: e.target.value})}
                      placeholder="10"
                    />
                    <span className="text-sm text-slate-600">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Desvios acima desse percentual ficam em amarelo/vermelho no dashboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Grupos de parâmetros operacionais em accordion */}
            <Accordion type="multiple" className="w-full">
              {PARAM_GROUPS.map((group) => {
                const filled = group.fields.filter(f => formData[f.key] !== '' && formData[f.key] !== null && formData[f.key] !== undefined).length;
                const total = group.fields.length;
                const isComplete = filled === total;
                const isEmpty = filled === 0;
                return (
                  <AccordionItem key={group.id} value={group.id} data-testid={`param-group-${group.id}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex flex-col items-start">
                          <span className="font-semibold text-base">{group.title}</span>
                          <span className="text-xs text-slate-500 font-normal">{group.description}</span>
                        </div>
                        <Badge
                          variant={isComplete ? "default" : isEmpty ? "outline" : "secondary"}
                          className={isComplete ? "bg-green-600" : ""}
                          data-testid={`param-group-${group.id}-badge`}
                        >
                          {filled} de {total}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        {group.fields.map((field) => (
                          <div key={field.key}>
                            <Label htmlFor={field.key}>
                              {field.label}
                              {field.unit && <span className="text-xs text-slate-500 ml-1">({field.unit})</span>}
                            </Label>
                            <Input
                              id={field.key}
                              data-testid={`param-input-${field.key}`}
                              type={field.type}
                              step={field.step}
                              value={formData[field.key] ?? ''}
                              onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                              placeholder={field.placeholder}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}

              {/* ===== 7º accordion — Itens de Almoxarifado (tabela editável) ===== */}
              <AccordionItem value="warehouse" data-testid="param-group-warehouse">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold text-base">🗄️ Itens de Almoxarifado</span>
                      <span className="text-xs text-slate-500 font-normal">
                        Cadastre itens com estoque mínimo, vida útil e destino padrão
                      </span>
                    </div>
                    <Badge
                      variant={warehouseItems.length > 0 ? "default" : "outline"}
                      className={warehouseItems.length > 0 ? "bg-green-600" : ""}
                      data-testid="param-group-warehouse-badge"
                    >
                      {warehouseItems.length} {warehouseItems.length === 1 ? 'item cadastrado' : 'itens cadastrados'}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-2 space-y-3">
                    <p className="text-xs text-slate-600">
                      O <strong>estoque atual</strong> será calculado automaticamente pelo Dashboard a partir dos RDOs.
                      Aqui cadastre apenas as <strong>regras</strong> do item.
                    </p>

                    {warehouseItems.length === 0 && (
                      <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg text-sm text-slate-500">
                        Nenhum item cadastrado. Clique em "Adicionar Item" para começar.
                      </div>
                    )}

                    {warehouseItems.map((item, idx) => (
                      <div
                        key={item.id}
                        className="border rounded-lg p-3 bg-slate-50 space-y-3"
                        data-testid={`warehouse-item-row-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-700">Item #{idx + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setWarehouseItems(prev => prev.filter((_, i) => i !== idx))}
                            data-testid={`warehouse-item-remove-${idx}`}
                            title="Remover item"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="md:col-span-2">
                            <Label className="text-xs">Nome *</Label>
                            <Input
                              value={item.name}
                              data-testid={`warehouse-item-name-${idx}`}
                              onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, name: e.target.value } : it))}
                              placeholder="Ex: Kit vedação pequeno"
                              className="bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Unidade *</Label>
                            <Select
                              value={item.unit}
                              onValueChange={(value) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, unit: value } : it))}
                            >
                              <SelectTrigger className="bg-white" data-testid={`warehouse-item-unit-${idx}`}>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                {WAREHOUSE_UNIT_OPTIONS.map(u => (
                                  <SelectItem key={u} value={u}>{u}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Estoque inicial</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.initial_stock}
                              data-testid={`warehouse-item-initial-${idx}`}
                              onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, initial_stock: e.target.value } : it))}
                              placeholder="50"
                              className="bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">⚠️ Estoque mínimo</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.min_stock}
                              data-testid={`warehouse-item-min-${idx}`}
                              onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, min_stock: e.target.value } : it))}
                              placeholder="4"
                              className="bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Vida útil — valor</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.lifespan_value}
                              data-testid={`warehouse-item-lifespan-value-${idx}`}
                              onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, lifespan_value: e.target.value } : it))}
                              placeholder="30"
                              className="bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Vida útil — unidade</Label>
                            <Select
                              value={item.lifespan_unit}
                              onValueChange={(value) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, lifespan_unit: value } : it))}
                            >
                              <SelectTrigger className="bg-white" data-testid={`warehouse-item-lifespan-unit-${idx}`}>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                {WAREHOUSE_LIFESPAN_UNIT_OPTIONS.map(u => (
                                  <SelectItem key={u} value={u}>{u}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Destino padrão</Label>
                            <Select
                              value={item.standard_destination}
                              onValueChange={(value) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, standard_destination: value } : it))}
                            >
                              <SelectTrigger className="bg-white" data-testid={`warehouse-item-destination-${idx}`}>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                {WAREHOUSE_DESTINATION_OPTIONS.map(o => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">
                              🛒 SKU no PROCUREMENT <span className="text-slate-500 font-normal">(opcional)</span>
                            </Label>
                            <Input
                              value={item.procurement_sku}
                              data-testid={`warehouse-item-sku-${idx}`}
                              onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, procurement_sku: e.target.value } : it))}
                              placeholder="Código no sistema de compras"
                              className="bg-white"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Observações</Label>
                          <Textarea
                            value={item.observations}
                            data-testid={`warehouse-item-observations-${idx}`}
                            onChange={(e) => setWarehouseItems(prev => prev.map((it, i) => i === idx ? { ...it, observations: e.target.value } : it))}
                            placeholder="Ex: fornecedor X, prazo 15 dias"
                            rows={2}
                            className="bg-white"
                          />
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setWarehouseItems(prev => [...prev, buildEmptyWarehouseItem()])}
                      data-testid="warehouse-item-add-btn"
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Item de Almoxarifado
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

          </div>

          <DialogFooter>
            <Button variant="outline" data-testid="cancel-client-btn" onClick={() => setShowClientModal(false)}>
              Cancelar
            </Button>
            <Button onClick={saveClient} data-testid="save-client-btn" className="bg-green-600 hover:bg-green-700">
              {editingClient ? 'Atualizar Cliente' : 'Criar Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Analytics (Fase B — Dashboard Visual) */}
      <Dialog open={showAnalyticsModal} onOpenChange={setShowAnalyticsModal}>
        <DialogContent className="max-w-7xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              📈 Dashboard de Análise — {selectedClient?.client_name}
            </DialogTitle>
            <DialogDescription>
              Comparativo padrão vs realizado, alertas de almoxarifado e indicadores de saúde do contrato
            </DialogDescription>
          </DialogHeader>
          <AnalyticsDashboard client={selectedClient} open={showAnalyticsModal} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParametersPanel;
